import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Fab,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UserAuthentication from '../../../../components/userAuthentication';
import TreatmentsModal from '../treatmentModal';
import ProcedureListRouter from '../procedures/procedureList/procedureListRouter';
import ApproveTreatmentModal from '../treatmentApproveModal';
import RefuseTreatmentModal from '../treatmentRefuseModal';
import FinishTreatmentModal from '../treatmentFinishModal';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from '../../../../components/loading';
import SuccessMessage from '../../../../components/successMessage';
import ErrorMessage from '../../../../components/errorMessage';
import { useClinic } from '../../../../hooks/useClinic';
import { useTreatments } from '../hooks/useTreatments';
import { useTreatmentActions } from '../hooks/useTreatmentActions';
import HowItWorksLink from '../../../../components/howItWorks/howItWorksLink';
import HowItWorksEmptyState from '../../../../components/howItWorks/howItWorksEmptyState';
import {
  TREATMENT_FLOW_FEATURE_KEY,
  TREATMENT_FLOW_SUBTITLE,
  TREATMENT_FLOW_TITLE,
  treatmentFlowStages,
} from '../treatmentFlow';
import { isApproved, isFinished, isRefused, statusChipColor } from '../treatmentStatus';

export default function TreatmentsListMobile() {
  const { patientId } = useParams();
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [treatments, setTreatments] = useState([]);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [treatmentForActions, setTreatmentForActions] = useState(null);
  const [isNewTreatmentModalOpen, setIsNewTreatmentModalOpen] = useState(false);
  const [anchorEl, setActionsMenuAnchorEl] = useState(null);
  const [showProcedureComponent, setShowProcedureComponent] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRefuseModalOpen, setIsRefuseModalOpen] = useState(false);
  const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);

  const { clinic } = useClinic();
  const usesInsurance = clinic?.usesInsurance ?? false;

  const {
    treatments: fetchedTreatments,
    isLoading: isTreatmentsLoading,
    refetch: getTreatmentsFromPatient,
  } = useTreatments(patientId);
  const { approve, refuse, finish, remove } = useTreatmentActions(patientId);

  useEffect(() => {
    setTreatments(fetchedTreatments);
  }, [fetchedTreatments]);

  const openTreatmentModal = () => setIsNewTreatmentModalOpen(true);
  const closeTreatmentModal = () => setIsNewTreatmentModalOpen(false);

  const setTreatmentClicked = (event, treatment) => {
    setActionsMenuAnchorEl(event.currentTarget);
    setTreatmentForActions(treatment);
    setSelectedTreatment(treatment);
  };

  const closeTreatmentActionsMenu = () => setActionsMenuAnchorEl(null);

  const onTreatmentSaved = (returnFromApi) => {
    if (returnFromApi.data) {
      setShowSuccessMessage(true);
      setSuccessMessage(returnFromApi.data);
    } else {
      setShowErrorMessage(true);
      setErrorMessage(returnFromApi.response?.data);
    }
    getTreatmentsFromPatient();
    closeTreatmentModal();
  };

  const onProcedureSaved = () => {
    getTreatmentsFromPatient();
  };

  const openProcedures = () => {
    setShowProcedureComponent(true);
    closeTreatmentActionsMenu();
  };

  const openApproveModal = () => {
    setIsApproveModalOpen(true);
    closeTreatmentActionsMenu();
  };

  const confirmTreatmentApproval = () => {
    setIsApproveModalOpen(false);

    setLoading(true);
    approve(selectedTreatment.treatmentId)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        setLoading(false);
        getTreatmentsFromPatient();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      });
  };

  const openRefuseModal = () => {
    setIsRefuseModalOpen(true);
    closeTreatmentActionsMenu();
  };

  const confirmTreatmentRefusal = (refusalReason) => {
    setIsRefuseModalOpen(false);

    setLoading(true);
    refuse(selectedTreatment.treatmentId, refusalReason)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        setLoading(false);
        getTreatmentsFromPatient();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      });
  };

  const openFinishModal = () => {
    setIsFinishModalOpen(true);
    closeTreatmentActionsMenu();
  };

  const confirmTreatmentConclusion = (finishDate) => {
    setIsFinishModalOpen(false);

    setLoading(true);
    finish(selectedTreatment.treatmentId, finishDate)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        setLoading(false);
        getTreatmentsFromPatient();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      });
  };

  const deleteSelectedTreatment = () => {
    closeTreatmentActionsMenu();
    setLoading(true);
    remove(selectedTreatment.treatmentId)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        setLoading(false);
        setShowProcedureComponent(false);
        getTreatmentsFromPatient();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      });
  };

  const printTreatment = () => {
    window.open(`/treatment-to-print/${selectedTreatment.treatmentId}`, '_blank');
  };

  const printEvolutionSheet = () => {
    closeTreatmentActionsMenu();
    window.open(`/treatment-evolution-to-print/${selectedTreatment.treatmentId}`, '_blank');
  };

  const treatmentClick = (treatment) => {
    setSelectedTreatment(treatment);
    setShowProcedureComponent(true);
  };

  return (
    <>
      <Loading isToHidden={isLoading === false && isTreatmentsLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}
      <UserAuthentication />

      <Box>
        {showProcedureComponent ? (
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => setShowProcedureComponent(false)}
              sx={{ mb: 1 }}
            >
              Voltar aos Tratamentos
            </Button>
            <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent>
                <ProcedureListRouter
                  treatment={selectedTreatment}
                  onProcedureSaved={onProcedureSaved}
                />
              </CardContent>
            </Card>
          </Box>
        ) : (
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Tratamentos
                </Typography>
              }
              subheader={
                <HowItWorksLink
                  featureKey={TREATMENT_FLOW_FEATURE_KEY}
                  title={TREATMENT_FLOW_TITLE}
                  subtitle={TREATMENT_FLOW_SUBTITLE}
                  stages={treatmentFlowStages}
                />
              }
              action={
                <Fab
                  color="primary"
                  size="small"
                  aria-label="adicionar tratamento"
                  onClick={openTreatmentModal}
                  sx={{ boxShadow: 2 }}
                >
                  <AddIcon />
                </Fab>
              }
            />
            <CardContent>
              {!isLoading && treatments.length === 0 ? (
                <HowItWorksEmptyState
                  title="Nenhum tratamento cadastrado ainda"
                  subtitle={TREATMENT_FLOW_SUBTITLE}
                  stages={treatmentFlowStages}
                  actionLabel="Adicionar Tratamento"
                  onAction={openTreatmentModal}
                />
              ) : (
                <List>
                  {treatments.map((treatment) => (
                    <React.Fragment key={treatment.treatmentId}>
                      <ListItem
                        selected={selectedTreatment?.treatmentId === treatment.treatmentId}
                        secondaryAction={
                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation();
                              setTreatmentClicked(event, treatment);
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={treatment.description}
                          secondary={`Profissional: ${treatment.dentistName} Valor: ${treatment.formattedTotalAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`}
                          onClick={() => treatmentClick(treatment)}
                          primaryTypographyProps={{ fontWeight: 'bold', mb: 1 }}
                        />
                        <Chip
                          label={treatment.status}
                          color={statusChipColor(treatment.status)}
                          size="small"
                          sx={{ ml: 2 }}
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      {/* {!showProcedureComponent && (
                <Fab
                    color="primary"
                    aria-label="adicionar tratamento"
                    sx={{ position: 'fixed', bottom: 24, right: 24 }}
                    onClick={openTreatmentModal}
                >
                    <AddIcon />
                </Fab>
            )} 
            */}

      <Menu anchorEl={anchorEl} onClose={closeTreatmentActionsMenu} open={Boolean(anchorEl)}>
        <MenuItem onClick={openProcedures}>Ver Procedimentos</MenuItem>
        <MenuItem onClick={printTreatment}>
          {isApproved(treatmentForActions) ? 'Imprimir Tratamento' : 'Imprimir Orçamento'}
        </MenuItem>
        <Tooltip
          placement="left"
          title={isApproved(treatmentForActions) ? 'Este tratamento já foi aprovado.' : ''}
        >
          <span>
            <MenuItem onClick={openApproveModal} disabled={isApproved(treatmentForActions)}>
              Aprovar Tratamento
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip
          placement="left"
          title={
            isApproved(treatmentForActions)
              ? 'Este tratamento já foi aprovado.'
              : isRefused(treatmentForActions)
                ? 'Este orçamento já está marcado como não aprovado.'
                : ''
          }
        >
          <span>
            <MenuItem
              onClick={openRefuseModal}
              disabled={isApproved(treatmentForActions) || isRefused(treatmentForActions)}
            >
              Marcar como Não Aprovado
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem onClick={printEvolutionSheet}>Imprimir Ficha de Evolução Clínica</MenuItem>
        <Tooltip
          placement="left"
          title={isFinished(treatmentForActions) ? 'Este tratamento já foi concluído.' : ''}
        >
          <span>
            <MenuItem onClick={openFinishModal} disabled={isFinished(treatmentForActions)}>
              Concluir Tratamento
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip
          placement="left"
          title={
            isApproved(treatmentForActions)
              ? 'Não é possível deletar um tratamento já aprovado.'
              : ''
          }
        >
          <span>
            <MenuItem
              onClick={deleteSelectedTreatment}
              sx={{ color: 'error.main' }}
              disabled={isApproved(treatmentForActions)}
            >
              Deletar
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>

      <TreatmentsModal
        open={isNewTreatmentModalOpen}
        onCloseTreatmentModal={closeTreatmentModal}
        onTreatmentSaved={onTreatmentSaved}
        usesInsurance={usesInsurance}
      />

      <ApproveTreatmentModal
        open={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        onConfirm={confirmTreatmentApproval}
        treatmentDescription={treatmentForActions?.description || ''}
      />

      <RefuseTreatmentModal
        open={isRefuseModalOpen}
        onClose={() => setIsRefuseModalOpen(false)}
        onConfirm={confirmTreatmentRefusal}
        treatmentDescription={treatmentForActions?.description || ''}
      />

      <FinishTreatmentModal
        open={isFinishModalOpen}
        onClose={() => setIsFinishModalOpen(false)}
        onConfirm={confirmTreatmentConclusion}
        treatmentDescription={treatmentForActions?.description || ''}
      />
    </>
  );
}
